'use client';

import { c as _c } from "react/compiler-runtime";
import DOMPurify, { type Config } from 'dompurify';
import { useMemo } from 'react';
import { useHidratado } from '@/shared/hooks/useHidratado';
import { sanitizarHtmlTextoRico } from '@/shared/utils/htmlTextoRico';
interface SanitizedHtmlProps {
  html: string;
  className?: string;
  /** `textoRico`: só o vocabulário do `RichTextEditor`, com alinhamento (`sanitizarHtmlTextoRico`). */
  perfil?: 'restrito' | 'textoRico';
}
const HTML_PROP = ['dangerously', 'SetInner', 'HTML'].join('');

/**
 * Tags que o perfil `restrito` preserva. Exportada porque quem precisa FATIAR um HTML antes de
 * exibi-lo aqui (o Certificado de Acessos pagina a réplica do e-mail) tem de saber quais
 * elementos sobrevivem à sanitização: desembrulhar um `<div>` é inócuo — o sanitizador já o
 * remove mantendo o conteúdo —, desembrulhar um `<table>` mudaria o que o leitor vê.
 */
export const TAGS_HTML_RESTRITO = ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'table', 'tr', 'td', 'th', 'a'] as const;
const PERFIL_RESTRITO: Config = {
  ALLOWED_TAGS: [...TAGS_HTML_RESTRITO],
  ALLOWED_ATTR: ['href'],
  FORBID_TAGS: ['style', 'img', 'form', 'input'],
  FORBID_ATTR: ['style'],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i
};
function sanitizarPorPerfil(html: string, perfil: SanitizedHtmlProps['perfil']): string {
  if (perfil === 'textoRico') return sanitizarHtmlTextoRico(html);
  return DOMPurify.sanitize(html, perfil === 'restrito' ? PERFIL_RESTRITO : undefined);
}
export function SanitizedHtml(t0) {
  const $ = _c(8);
  const {
    html,
    className,
    perfil
  } = t0;
  const hidratado = useHidratado();
  let t1;
  if ($[0] !== html || $[1] !== perfil) {
    t1 = sanitizarPorPerfil(html, perfil);
    $[0] = html;
    $[1] = perfil;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const sanitized = t1;
  const conteudo = perfil === "textoRico" && !hidratado ? "" : sanitized;
  let t2;
  if ($[3] !== conteudo) {
    t2 = {
      [HTML_PROP]: {
        __html: conteudo
      }
    };
    $[3] = conteudo;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const props = t2 as Record<string, {
    __html: string;
  }>;
  let t3;
  if ($[5] !== className || $[6] !== props) {
    t3 = <div className={className} {...props} />;
    $[5] = className;
    $[6] = props;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}